/**
 * Leads Management Page
 *
 * Enterprise features:
 * - Real-time lead stats with polling
 * - Infinite scroll leads list
 * - Lead scoring and status management
 * - Export functionality
 * - Lead detail drawer
 */

import { useState, useMemo, useCallback } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import Modal from '../../components/modals/Modal';
import { leadsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useInfiniteQuery, useMutation, useIntersectionObserver } from '../../hooks/useDataFetch';
import './LazyMarketing.css';

// Constants
const STATS_REFRESH_INTERVAL = 60000; // 60 seconds
const LEADS_PAGE_SIZE = 20;

const Leads = () => {
  const { showToast } = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch lead stats with polling
  const {
    data: statsData,
    isLoading: loadingStats,
    refetch: refetchStats,
  } = useQuery(
    () => leadsApi.list({ limit: 1, includeStats: true }),
    {
      key: 'leads:stats',
      cacheTTL: 30000,
      refetchInterval: STATS_REFRESH_INTERVAL,
    }
  );

  // Fetch leads with cursor pagination
  const {
    data: leads,
    isLoading: loadingLeads,
    isFetchingMore,
    hasMore,
    fetchMore,
    refetch: refetchLeads,
  } = useInfiniteQuery(
    (cursor) => leadsApi.list({
      cursor,
      limit: LEADS_PAGE_SIZE,
      status: statusFilter === 'all' ? undefined : statusFilter,
      sort: 'score:desc',
    }),
    {
      key: `leads:list:${statusFilter}`,
      getNextCursor: (page) => page?.nextCursor || page?.meta?.nextCursor,
      cacheTTL: 30000,
    }
  );

  // Update lead mutation
  const updateMutation = useMutation(
    ({ id, data }) => leadsApi.update(id, data),
    {
      onSuccess: () => {
        showToast('Lead updated', 'success');
        refetchLeads();
        setShowDetailModal(false);
      },
      onError: (error) => {
        showToast(error.message || 'Failed to update lead', 'error');
      },
    }
  );

  // Infinite scroll observer
  const { ref: loadMoreRef } = useIntersectionObserver({
    onIntersect: () => {
      if (hasMore && !isFetchingMore) {
        fetchMore();
      }
    },
    enabled: hasMore && !isFetchingMore,
  });

  // Compute stats
  const stats = useMemo(() => {
    const data = statsData || {};
    const meta = data.stats || data.meta || {};

    return [
      {
        label: 'Total Leads',
        value: formatNumber(meta.total || meta.totalLeads || 0),
        change: formatChange(meta.totalChange),
        changeType: getChangeType(meta.totalChange),
        icon: 'users',
      },
      {
        label: 'Qualified',
        value: formatNumber(meta.qualified || 0),
        change: formatChange(meta.qualifiedChange),
        changeType: getChangeType(meta.qualifiedChange),
        icon: 'check-circle',
      },
      {
        label: 'Conversion Rate',
        value: `${meta.conversionRate || 0}%`,
        change: formatChange(meta.conversionChange),
        changeType: getChangeType(meta.conversionChange),
        icon: 'trending-up',
      },
      {
        label: 'Avg. Score',
        value: meta.avgScore || 0,
        change: formatChange(meta.scoreChange),
        changeType: getChangeType(meta.scoreChange),
        icon: 'star',
      },
    ];
  }, [statsData]);

  // Extract leads list
  const leadsList = useMemo(() => {
    return leads || [];
  }, [leads]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetchStats();
    refetchLeads();
    showToast('Leads refreshed', 'success');
  }, [refetchStats, refetchLeads, showToast]);

  const handleExport = useCallback(async () => {
    try {
      showToast('Preparing export...', 'info');
      const data = await leadsApi.list({ format: 'csv', limit: 10000 });

      const blob = new Blob([data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast('Export downloaded', 'success');
    } catch (error) {
      showToast(error.message || 'Export failed', 'error');
    }
  }, [showToast]);

  const handleLeadClick = useCallback((lead) => {
    setSelectedLead(lead);
    setShowDetailModal(true);
  }, []);

  const handleStatusChange = useCallback((leadId, newStatus) => {
    updateMutation.mutate({ id: leadId, data: { status: newStatus } });
  }, [updateMutation]);

  const handleFilterChange = useCallback((filter) => {
    setStatusFilter(filter);
  }, []);

  // Helpers
  function formatNumber(num) {
    if (!num) return '0';
    return num.toLocaleString();
  }

  function formatChange(value) {
    if (value === null || value === undefined) return undefined;
    return `${value >= 0 ? '+' : ''}${value}`;
  }

  function getChangeType(value) {
    if (value === null || value === undefined) return 'neutral';
    return value >= 0 ? 'positive' : 'negative';
  }

  function getStatusStyle(status) {
    switch (status?.toLowerCase()) {
      case 'hot':
        return { background: 'var(--danger-soft)', color: 'var(--danger)' };
      case 'warm':
        return { background: 'var(--warning-soft)', color: 'var(--warning)' };
      case 'cold':
        return { background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' };
      case 'qualified':
        return { background: 'var(--success-soft)', color: 'var(--success)' };
      default:
        return { background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' };
    }
  }

  function getScoreColor(score) {
    if (score >= 80) return 'var(--success)';
    if (score >= 60) return 'var(--warning)';
    return 'var(--text-tertiary)';
  }

  const isLoading = loadingLeads && leadsList.length === 0;

  return (
    <div className="lm-page">
      {/* Header */}
      <div className="lm-page-header">
        <div>
          <h1 className="lm-page-title">Lead Management</h1>
          <p className="lm-page-subtitle">Track and qualify your leads</p>
        </div>
        <div className="lm-page-actions">
          <Button variant="secondary" icon="refresh" onClick={handleRefresh} disabled={loadingStats}>
            Refresh
          </Button>
          <Button variant="secondary" icon="download" onClick={handleExport}>
            Export
          </Button>
          <Button variant="primary" icon="plus">
            Add Lead
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="lm-stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loadingStats && !statsData} />
        ))}
      </div>

      {/* Filters */}
      <div className="lm-filters">
        <div className="filter-tabs">
          {['all', 'hot', 'warm', 'cold', 'qualified'].map((filter) => (
            <button
              key={filter}
              className={`filter-tab ${statusFilter === filter ? 'active' : ''}`}
              onClick={() => handleFilterChange(filter)}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Leads Grid */}
      <div className="lm-section">
        <div className="lm-section-header">
          <h3 className="lm-section-title">Recent Leads</h3>
          <span className="lm-section-count">
            {loadingLeads ? '...' : `${leadsList.length} leads`}
          </span>
        </div>

        {isLoading ? (
          <div className="lm-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="lm-grid-card skeleton">
                <div className="skeleton-header">
                  <div className="skeleton-line" style={{ width: '60%' }}></div>
                  <div className="skeleton-badge"></div>
                </div>
                <div className="skeleton-content">
                  <div className="skeleton-line" style={{ width: '80%' }}></div>
                </div>
              </div>
            ))}
          </div>
        ) : leadsList.length > 0 ? (
          <>
            <div className="lm-grid">
              {leadsList.map((lead) => (
                <div
                  key={lead.id}
                  className="lm-grid-card clickable"
                  onClick={() => handleLeadClick(lead)}
                >
                  <div className="lm-grid-card-header">
                    <span className="lm-grid-card-title">{lead.name || lead.email}</span>
                    <span className="lm-grid-card-badge" style={getStatusStyle(lead.status)}>
                      {lead.status}
                    </span>
                  </div>
                  <div className="lm-grid-card-content">
                    <p>{lead.company || 'Unknown Company'}</p>
                    <div className="lead-score">
                      <span className="score-label">Score:</span>
                      <span className="score-value" style={{ color: getScoreColor(lead.score) }}>
                        {lead.score || 0}
                      </span>
                      <div className="score-bar">
                        <div
                          className="score-fill"
                          style={{
                            width: `${lead.score || 0}%`,
                            background: getScoreColor(lead.score),
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="lm-grid-card-footer">
                    <span className="lead-source">{lead.source || 'Direct'}</span>
                    <span className="lead-date">
                      {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={loadMoreRef} className="lm-load-more">
                {isFetchingMore ? (
                  <div className="loading-spinner">Loading more...</div>
                ) : (
                  <span>Scroll for more</span>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="lm-empty">
            <Icon name="users" size="xl" />
            <p>No leads found</p>
            <Button variant="primary" icon="plus">Add your first lead</Button>
          </div>
        )}
      </div>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <Modal
          title="Lead Details"
          onClose={() => setShowDetailModal(false)}
        >
          <div className="lead-detail-modal">
            <div className="lead-detail-header">
              <div className="lead-avatar">
                {(selectedLead.name || selectedLead.email || '?')[0].toUpperCase()}
              </div>
              <div className="lead-info">
                <h3>{selectedLead.name || 'Unknown'}</h3>
                <p>{selectedLead.email}</p>
                <p>{selectedLead.company}</p>
              </div>
              <div className="lead-score-large" style={{ color: getScoreColor(selectedLead.score) }}>
                {selectedLead.score || 0}
              </div>
            </div>

            <div className="lead-detail-section">
              <h4>Status</h4>
              <div className="status-buttons">
                {['Hot', 'Warm', 'Cold', 'Qualified'].map((status) => (
                  <button
                    key={status}
                    className={`status-btn ${selectedLead.status === status ? 'active' : ''}`}
                    style={getStatusStyle(status)}
                    onClick={() => handleStatusChange(selectedLead.id, status)}
                    disabled={updateMutation.isLoading}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div className="lead-detail-section">
              <h4>Contact Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{selectedLead.email || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{selectedLead.phone || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Company</span>
                  <span className="detail-value">{selectedLead.company || '-'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Source</span>
                  <span className="detail-value">{selectedLead.source || '-'}</span>
                </div>
              </div>
            </div>

            <div className="lead-detail-actions">
              <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </Button>
              <Button variant="primary" icon="mail">
                Send Email
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Leads;
